<template>
  <DropDownButton
    v-if="hasDropDownItems"
    data-test="shipment-menu"
    :primary-button="primaryAction">
    <DropDownItem v-if="canCreateOrder">
      <RouterLink :to="newOrder.to">
        {{ newOrder.label }}
      </RouterLink>
    </DropDownItem>
    <DropDownItem>
      <RouterLink :to="regularShipping.to">
        {{ regularShipping.label }}
      </RouterLink>
    </DropDownItem>
    <template v-for="item in otherShippingActions">
      <DropDownItem
        v-if="item.enabled"
        :key="item.label">
        <ExternalLinkAnchor :href="item.href">
          {{ item.label }}
        </ExternalLinkAnchor>
      </DropDownItem>
    </template>
    <template v-if="socialShipping.enabled">
      <DropDownItemDivider />
      <DropDownItem>
        <ExternalLinkAnchor :href="socialShipping.href">
          {{ socialShipping.label }}
        </ExternalLinkAnchor>
      </DropDownItem>
    </template>
  </DropDownButton>
  <RouterLink
    v-else
    :to="regularShipping.to">
    <Button :variant="ButtonVariant.Secondary">
      {{ regularShipping.label }}
    </Button>
  </RouterLink>
</template>

<script setup lang="ts">
import {useProposition} from '@myparcel-frontend/proposition';
import {ExternalLinkAnchor, Button, ButtonVariant} from '@myparcel-frontend/ui';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {RouterLink} from 'vue-router';
import DropDownButton from '@/components/ui/Dropdown/DropDownButton/DropDownButton.vue';
import DropDownItem from '@/components/ui/Dropdown/DropDownItem/DropDownItem.vue';
import DropDownItemDivider from '@/components/ui/Dropdown/DropDownItemDivider/DropDownItemDivider.vue';
import useFeatures from '@/composables/useFeatures';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';
import {newOrderPanelRoute} from '@/views/Orders/OverviewMobile/NewOrderSidePanel/newOrderPanelRoute';

const {proposition} = useProposition();
const {t} = useI18n();
const {hasOrderManagement} = useOrderVersion();
const {isFeatureActive} = useFeatures();

// The new order sidepanel only exists in OverviewMobile, which the router loads behind this flag.
const canCreateOrder = computed(() => hasOrderManagement && isFeatureActive('page.orders.mobile'));

const regularShipping = computed(() => ({
  label: t('shipment.newShipment'),
  to: {name: 'shipments-create'},
}));

const newOrder = computed(() => ({
  label: t('orders.create.title'),
  to: newOrderPanelRoute,
}));

const primaryAction = computed(() => (canCreateOrder.value ? newOrder.value : regularShipping.value));

const socialShipping = computed(() => ({
  label: t('shipment.createSocialLink'),
  href: '/shipments?modal=social-shipping',
  enabled: proposition.value.shipments.enableSocialShipping,
}));

const otherShippingActions = computed(() => [
  {
    label: t('shipment.pallet.label'),
    href: '/shipments/pallet/create',
    enabled: proposition.value.shipments.enablePallet,
  },
  {
    label: t('shipment.return', 2),
    href: '/shipments/return/create',
    enabled: proposition.value.shipments.enableReturn,
  },
  {
    label: t('shipment.multiple'),
    href: '/shipments/import',
    enabled: proposition.value.shipments.enableCsvImport,
  },
  {
    label: t('shipment.importExport.title'),
    href: '/shipments/import-export',
    enabled: proposition.value.shipments.enableGoodsImportExport,
  },
]);

const hasDropDownItems = computed(() => {
  return (
    canCreateOrder.value || socialShipping.value.enabled || otherShippingActions.value.some((item) => item.enabled)
  );
});
</script>
